import { __awaiter } from 'tslib';
import * as Long from 'long';
import { im_proto } from '../proto/index.js';
import { ErrorType, PushPartDisableConfigWhiteListKey, InfoKeys, IMEvent, FlightStatus, MessageSource, PushStatus } from '../core/consts.js';
import { Logger } from '../core/metrics/logger.js';
import { BaseError } from '../core/errors/BaseError.js';
import '../core/metrics/monitor.js';
import '../core/network/NetworkItem.js';
import { ServiceIdentity } from '../core/service/RuntimeContext.js';
import { Conversation } from '../core/model/Conversation.js';
import { Message, PropertyStatus } from '../core/model/Message.js';
import 'js-md5';
import '../core/utils/lz4.js';
import '../core/utils/serializer.js';
import '../adapter/manager/AdapterManager.js';
import '../adapter/manager/types.js';
import '../core/utils/ticker.js';
import '../adapter/abstract/IMNetApi.js';
import '../core/options.js';
import { uuidv4 } from '../core/utils/uuid.js';
import '../core/service/ConversationManager.js';
import '../core/service/MessageManager.js';
import { Participant } from '../core/model/Participant.js';
import '@byted/uc-secure-sdk';
import '../core/plugin/DbProxy.js';
import * as JSONLong from 'json-long';
import '../core/metrics/trackerTypes.js';
import { BasePlugin } from '../core/plugin/BasePlugin.js';
import { groupBy, chunkArray, string2LongAsBit } from '../core/utils/tools.js';
import '../core/model/CommandMessage.js';
import { MessageProperty } from '../core/model/MessageProperty.js';
import { ExtensionApiManager } from './api/ExtensionApiManager.js';
import { BroadcastManager } from './service/BroadcastManager.js';
import { PropertyCmdProcessor } from './processor/PropertyCmdProcessor.js';
import { BlockCmdProcessor } from './processor/BlockCmdProcessor.js';
import { PinCmdProcessor } from './processor/PinCmdProcessor.js';
import { P2PMessage } from './model/P2PMessage.js';
import { FileExtKey } from '../multimedia/interfaces.js';

class ExtensionPlugin extends BasePlugin {
    // #region Base Extensions
    /**
     * 用户自己退群
     * @param params.conversation 所在会话
     */
    leaveConversation(params) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.api.LeaveConversation({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                inboxType: params.conversation.inboxType
            });
            this.resolve(ServiceIdentity.ParticipantManager).delete(params.conversation.id, [this.ctx.option.userId]);
        });
    }
    /**
     * 删除消息
     * @param params.message 删除的消息
     * @param params.localOnly 仅删除本地，不同步到服务端
     */
    deleteMessage(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!params.message.serverId) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.MessageNotReady,
                    msg: `message ${params.message} is not ready`,
                    reachServer: false,
                    sender: this
                });
            }
            try {
                this.resolve(ServiceIdentity.MessageManager).delete(params.message.conversationId, params.message.serverId);
                if (params.localOnly) {
                    return;
                }
                const conv = this.resolve(ServiceIdentity.ConversationManager).get(params.message.conversationId);
                yield this.api.DeleteMessage({
                    conversationId: params.message.conversationId,
                    conversationShortId: Long.fromString(params.message.conversationShortId),
                    conversationType: params.message.conversationType,
                    serverId: Long.fromString(params.message.serverId),
                    inboxType: conv.inboxType
                });
            }
            catch (error) {
                // istanbul ignore next
                Logger.ctxWarn(this.ctx, 'delete message error, may lost server operation', error);
            }
        });
    }
    /**
     * 删除会话
     * @param params.conversation 删除的会话
     * @param params.localOnly 布尔值 仅删除本地，不同步到服务端 不传默认本地和server都删除
     */
    deleteConversation(params) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.resolve(ServiceIdentity.ConversationManager).delete(params.conversation.id);
                if (params.localOnly) {
                    // istanbul ignore next
                    return;
                }
                const lastIndex = params.conversation.lastMessageIndex;
                yield this.api.MarkConversationDelete({
                    conversationId: params.conversation.id,
                    conversationShortId: Long.fromString(params.conversation.shortId),
                    conversationType: params.conversation.type,
                    lastPullIndex: lastIndex,
                    inboxType: params.conversation.inboxType
                });
            }
            catch (err) {
                Logger.ctxWarn(this.ctx, 'delete conversation error, may lost server operation', err);
            }
        });
    }
    /**
     * 获取顶置会话
     * @param params.cursor 拉取游标 不传默认0
     * @param params.limit 拉取条数
     * @param params.inboxType 信箱类型
     */
    getConversationListByTop(params = {}) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (params.inboxType === undefined) {
                params.inboxType = this.resolve(ServiceIdentity.InboxType).getDefaultInbox();
            }
            let conversation = [];
            const response = yield this.api.GetConversationInfoListByTopV2({
                cursor: Long.fromValue((_a = params.cursor) !== null && _a !== void 0 ? _a : '0'),
                limit: (_b = params.limit) !== null && _b !== void 0 ? _b : 10,
                inboxType: params.inboxType
            });
            const res = (_c = response.body) === null || _c === void 0 ? void 0 : _c.get_conversation_info_list_by_top_v2_body;
            const convs = res.conversation_info_list;
            const cs = convs
                .map(c => Conversation.fromServerConversation(this.ctx, c, response.log_id))
                .map(c => this.resolve(ServiceIdentity.ConversationManager).upsert(c));
            conversation = conversation.concat(cs);
            return {
                conversation,
                hasMore: res === null || res === void 0 ? void 0 : res.has_more,
                cursor: res === null || res === void 0 ? void 0 : res.next_cursor
            };
        });
    }
    /**
     * 获取收藏会话
     * @param params.cursor 拉取游标 不传默认0
     * @param params.limit 拉取条数
     * @param params.inboxType 信箱类型
     */
    getConversationListByFavorite(params = {}) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            if (params.inboxType === undefined) {
                params.inboxType = this.resolve(ServiceIdentity.InboxType).getDefaultInbox();
            }
            let conversation = [];
            const response = yield this.api.GetConversationInfoListByFavoriteV2({
                cursor: Long.fromValue((_a = params.cursor) !== null && _a !== void 0 ? _a : '0'),
                limit: (_b = params.limit) !== null && _b !== void 0 ? _b : 10,
                inboxType: params.inboxType
            });
            const res = (_c = response.body) === null || _c === void 0 ? void 0 : _c.get_conversation_info_list_by_favorite_v2_body;
            const convs = res === null || res === void 0 ? void 0 : res.conversation_info_list;
            const cs = convs
                .map(c => Conversation.fromServerConversation(this.ctx, c, response.log_id))
                .map(c => this.resolve(ServiceIdentity.ConversationManager).upsert(c));
            conversation = conversation.concat(cs);
            return {
                conversation,
                hasMore: res === null || res === void 0 ? void 0 : res.has_more,
                cursor: res === null || res === void 0 ? void 0 : res.next_cursor
            };
        });
    }
    /**
     * 添加群成员
     * @param params.conversation 需要添加成员的会话
     * @param params.participant 添加群成员id
     * @param params.bizExt 自定义拓展信息
     */
    addParticipants(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const arrayParticipant = Array.isArray(params.participant) ? params.participant : [params.participant];
            const resp = yield this.api.AddConversationParticipants({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                participants: arrayParticipant.map(s => Long.fromValue(s)),
                bizExt: params.bizExt,
                inboxType: params.conversation.inboxType
            });
            if ((resp === null || resp === void 0 ? void 0 : resp.status) !== 0) {
                // istanbul ignore next
                return {
                    success: false,
                    failedParticipant: arrayParticipant,
                    payload: params.conversation,
                    checkCode: resp.check_code,
                    checkMsg: resp.check_message,
                    statusCode: resp.status,
                    statusMsg: resp.extra_info
                };
            }
            const failedUser = (_b = (_a = resp.failed_participants) === null || _a === void 0 ? void 0 : _a.map(u => u.toString())) !== null && _b !== void 0 ? _b : [];
            return {
                success: true,
                failedParticipant: failedUser,
                payload: params.conversation,
                checkCode: resp.check_code,
                checkMsg: resp.check_message,
                statusCode: resp.status,
                statusMsg: resp.extra_info
            };
        });
    }
    /**
     * 移除群成员
     * @param params.conversation 需要删除成员的会话
     * @param params.participant 删除群成员uid
     * @param params.bizExt 自定义拓展信息
     */
    removeParticipants(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!Array.isArray(params.participant)) {
                params.participant = [params.participant];
            }
            const allParticipants = params.participant.map((s) => Long.fromString(typeof s === 'string' ? s : s.userId));
            const resp = yield this.api.RemoveConversationParticipants({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                participants: allParticipants,
                bizExt: params.bizExt,
                inboxType: params.conversation.inboxType
            });
            if ((resp === null || resp === void 0 ? void 0 : resp.status) !== 0) {
                // istanbul ignore next
                return {
                    success: false,
                    // istanbul ignore next
                    failedParticipant: allParticipants.map(u => u.toString()),
                    body: resp,
                    payload: params.conversation,
                    checkCode: resp.check_code,
                    checkMsg: resp.check_message,
                    statusCode: resp.status,
                    statusMsg: resp.extra_info
                };
            }
            const successUser = [];
            const failedUser = (_b = (_a = resp.failed_participants) === null || _a === void 0 ? void 0 : _a.map(u => u.toString())) !== null && _b !== void 0 ? _b : [];
            allParticipants.forEach(u => {
                if (!failedUser.includes(u.toString())) {
                    successUser.push(u.toString());
                }
            });
            return {
                success: true,
                body: resp,
                failedParticipant: failedUser,
                payload: params.conversation,
                checkCode: resp.check_code,
                checkMsg: resp.check_message,
                statusCode: resp.status,
                statusMsg: resp.extra_info
            };
        });
    }
    /**
     * 修改群成员
     * @param params.conversation 会话
     * @param params.userId 成员的userid
     * @param params.role 成员角色 0普通成员 1群主
     * @param params.alias 成员的群昵称
     * @param params.bizExt 自定义拓展信息
     */
    updateParticipant(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield this.api.UpdateConversationParticipant({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                role: params.role,
                alias: params.alias,
                bizExt: params.bizExt,
                inboxType: params.conversation.inboxType,
                userId: Long.fromValue(params.userId)
            });
            const resBody = (_a = res === null || res === void 0 ? void 0 : res.body) === null || _a === void 0 ? void 0 : _a.update_conversation_participant_body;
            if ((res === null || res === void 0 ? void 0 : res.status_code) === 0 && ((_b = resBody === null || resBody === void 0 ? void 0 : resBody.participant) === null || _b === void 0 ? void 0 : _b.user_id) && (res === null || res === void 0 ? void 0 : res.log_id)) {
                const p = Participant.fromServerParticipant(this.ctx, resBody.participant, params.conversation, res.log_id);
                return {
                    success: true,
                    payload: p,
                    checkCode: resBody.check_code,
                    checkMsg: resBody.check_message,
                    statusCode: res.status_code,
                    statusMsg: resBody.extra_info,
                    body: resBody
                };
            }
            // istanbul ignore next
            return {
                success: false,
                payload: null,
                checkCode: resBody.check_code,
                checkMsg: resBody.check_message,
                statusCode: res.status_code,
                statusMsg: resBody.extra_info,
                body: resBody
            };
        });
    }
    /**
     * 设置会话置顶、免打扰、收藏等
     * @param params.conversation 会话
     * @param params.stickOnTop 是否置顶
     * @param params.mute 是否免打扰
     * @param params.favorite 是否收藏
     */
    setConversationSettingInfo(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.api.SetConversationSettingInfo({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                stickOnTop: params.stickOnTop,
                mute: params.mute,
                favorite: params.favorite,
                inboxType: params.conversation.inboxType,
                pushStatus: params.pushStatus
            });
            if ((resp === null || resp === void 0 ? void 0 : resp.status) !== 0) {
                // istanbul ignore next
                return {
                    success: false,
                    payload: params.conversation,
                    checkCode: resp.check_code,
                    checkMsg: resp.check_message,
                    statusCode: resp.status,
                    statusMsg: resp.extra_info
                };
            }
            yield this.resolve(ServiceIdentity.ConversationManager).refreshAsync(params.conversation);
            return {
                success: true,
                payload: params.conversation,
                checkCode: resp.check_code,
                checkMsg: resp.check_message,
                statusCode: resp.status,
                statusMsg: resp.extra_info
            };
        });
    }
    /**
     * 设置群公告、头像、群名
     * @param params.conversation 会话
     * @param params.name 群名
     * @param params.desc 群描述
     * @param params.icon 群头像url链接
     * @param params.notice 群公告
     * @param params.ext 扩展字段，落到中台数据库
     * @param params.bizExt 扩展字段，在调用链中存在，业务自己处理，不落到中台数据库
     */
    setConversationCoreInfo(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.api.SetConversationCoreInfo({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                name: params.name,
                desc: params.desc,
                icon: params.icon,
                notice: params.notice,
                ext: params.ext,
                bizExt: params.bizExt,
                inboxType: params.conversation.inboxType
            });
            if ((resp === null || resp === void 0 ? void 0 : resp.status) !== 0) {
                // istanbul ignore next
                return {
                    success: false,
                    payload: params.conversation,
                    checkCode: resp.check_code,
                    checkMsg: resp.check_message,
                    statusCode: resp.status,
                    statusMsg: resp.extra_info
                };
            }
            yield this.resolve(ServiceIdentity.ConversationManager).refreshAsync(params.conversation);
            return {
                success: true,
                payload: params.conversation,
                checkCode: resp.check_code,
                checkMsg: resp.check_message,
                statusCode: resp.status,
                statusMsg: resp.extra_info
            };
        });
    }
    /**
     * 更新ext拓展信息因为 settingExt 和 coreExt 的区别，更新扩展信息有两个方法
     * @param params.conversation 会话
     * @param params.ext 拓展信息
     */
    upsertConversationSettingExtInfo(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.api.UpsertConversationSettingExtInfo({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                ext: params.ext,
                inboxType: params.conversation.inboxType
            });
            if ((resp === null || resp === void 0 ? void 0 : resp.status) !== 0) {
                // istanbul ignore next
                return {
                    success: false,
                    payload: params.conversation,
                    checkCode: resp.check_code,
                    checkMsg: resp.check_message,
                    statusCode: resp.status,
                    statusMsg: resp.extra_info
                };
            }
            yield this.resolve(ServiceIdentity.ConversationManager).refreshAsync(params.conversation);
            return {
                success: true,
                payload: params.conversation,
                checkCode: resp.check_code,
                checkMsg: resp.check_message,
                statusCode: resp.status,
                statusMsg: resp.extra_info
            };
        });
    }
    /**
     * 设置会话地打扰模式配置
     * @param params.conversation 会话
     * @param params.config 配置
     */
    setConversationWeakMuteConfig(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const payload = JSONLong.stringify({
                [PushPartDisableConfigWhiteListKey]: {
                    white_uids: (_a = params.config.whiteUids) !== null && _a !== void 0 ? _a : [],
                    white_msg_types: (_b = params.config.whiteMsgTypes) !== null && _b !== void 0 ? _b : []
                }
            });
            return this.upsertConversationSettingExtInfo({
                conversation: params.conversation,
                ext: {
                    [InfoKeys.PushPartDisableConfig]: payload
                }
            });
        });
    }
    /**
     * 更新ext拓展信息
     * @param params.conversation 会话
     * @param params.ext 拓展信息
     */
    upsertConversationCoreExtInfo(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.api.UpsertConversationCoreExtInfo({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                ext: params.ext,
                inboxType: params.conversation.inboxType
            });
            if ((resp === null || resp === void 0 ? void 0 : resp.status) !== 0) {
                // istanbul ignore next
                return {
                    success: false,
                    payload: params.conversation,
                    checkCode: resp.check_code,
                    checkMsg: resp.check_message,
                    statusCode: resp.status,
                    statusMsg: resp.extra_info
                };
            }
            yield this.resolve(ServiceIdentity.ConversationManager).refreshAsync(params.conversation);
            return {
                success: true,
                payload: params.conversation,
                checkCode: resp.check_code,
                checkMsg: resp.check_message,
                statusCode: resp.status,
                statusMsg: resp.extra_info
            };
        });
    }
    // #region Read Receipt Extensions
    /**
     * 获取会话内某条消息各个成员的读取状态 返回已读成员数组 和应读成员数组（会话内除了自己的成员）
     * @param params.message 消息
     * @param params.batchFetch
     * @param params.syncFromServer 是否从服务端获取 不能太频繁用 syncFromServer
     */
    getMessageReadReceipt(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (params.message.isRecalled || !params.message.isFromMe || params.message.isOffline) {
                // 过滤掉本地的消息, 被撤回的消息和其他人发的消息
                return {
                    finishedParticipantIds: [],
                    expectedParticipantIds: []
                };
            }
            const conv = this.resolve(ServiceIdentity.ConversationManager).get(params.message.conversationId);
            let participants = this.resolve(ServiceIdentity.ParticipantManager).getRaw(conv.id);
            if (params.syncFromServer || participants.length === 0) {
                // 没有成员则说明之前没有拉取过 (会话不可能没有成员), 必须从服务端同步
                // 如果是单聊并且成员数是 0, 那么阻塞获取一次
                // 是 2 之后调多少次结果都不会变, 所以不需要调了
                if (conv.type === im_proto.ConversationType.ONE_TO_ONE_CHAT && participants.length === 0) {
                    /* istanbul ignore next */
                    yield this.getConversationParticipants({
                        conversation: conv
                    });
                }
                // 群聊需要不同的策略
                if (conv.type === im_proto.ConversationType.GROUP_CHAT) {
                    // 如果没有成员, 阻塞获取一次
                    if (participants.length === 0) {
                        yield this.getConversationParticipants({
                            conversation: conv
                        });
                    }
                    else {
                        // 已经有成员了, 那么异步获取, 暂时用目前的成员计算已读, 下次就会刷新
                        void this.getConversationParticipants({
                            conversation: conv
                        });
                    }
                }
                yield this.updateConversationReadReceipt({
                    conversation: conv,
                    batchFetch: Boolean(params.batchFetch)
                }); // 依赖成员列表, 需要放在后面
            }
            // 重新获取一次
            participants = this.resolve(ServiceIdentity.ParticipantManager).getRaw(conv.id);
            if (participants.length === 0) {
                // 兜底
                /* istanbul ignore next */
                return {
                    finishedParticipantIds: [],
                    expectedParticipantIds: []
                };
            }
            if (!params.message.indexInConversation) {
                // 消息没收到 push, 或者被自见了
                /* istanbul ignore next */
                return {
                    finishedParticipantIds: [],
                    expectedParticipantIds: participants.filter(m => m.userId !== this.ctx.option.userId).map(m => m.userId)
                };
            }
            const finished = [];
            const expected = [];
            participants.forEach(val => {
                if (val.minIndex.gt(params.message.indexInConversation)) {
                    // 退群设置了 minIndex 之后跳过
                    /* istanbul ignore next */
                    return;
                }
                if (val.userId !== this.ctx.option.userId) {
                    // 需要去掉自己, 下同
                    expected.push(val.userId.toString());
                }
                if ((val.readOrder.gte(params.message.orderInConversation) ||
                    val.readIndex.gte(params.message.indexInConversation)) &&
                    val.userId !== this.ctx.option.userId) {
                    // 先用 readOrder 算
                    finished.push(val.userId.toString());
                }
            });
            return {
                finishedParticipantIds: finished,
                expectedParticipantIds: expected
            };
        });
    }
    /**
     * 获取群成员列表 返回sdk本地维护的成员列表
     * 如果还有更多数据，sdk 会在返回本地数据之后，非阻塞的请求一次服务端接口，
     * 拉取下一页(50条)数据，然后更新本地数据。这样业务方下一次调用此方法时，会同步的返回本地已经获取到的全量数据。
     * 业务方可以用 Conversation.participantCount 拿到会话成员的总数量，
     * 然后对比 getConversationParticipants 返回的成员数量，判断有没有更多数据。
     * @param params.conversation 会话
     */
    getConversationParticipants(params) {
        return this.resolve(ServiceIdentity.ParticipantManager).getParticipant(params.conversation);
    }
    /**
     * 获取群成员列表 此方法会在 sdk 内部循环调用分页接口，直至没有更多数据，然后把所有群成员数据一次返回。当群成员较多时，此方法耗时会比较久。
     * @param params.conversation 会话
     */
    getConversationParticipantsAsync(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            let hasMore = true;
            let cursor = Long.ZERO;
            const limit = 50;
            while (hasMore) {
                const resp = yield this.getConversationParticipantsByPage({
                    cursor,
                    limit,
                    conversation: params.conversation
                });
                hasMore = (_a = resp === null || resp === void 0 ? void 0 : resp.hasMore) !== null && _a !== void 0 ? _a : false;
                cursor = (_b = resp === null || resp === void 0 ? void 0 : resp.cursor) !== null && _b !== void 0 ? _b : Long.ZERO;
            }
            return this.resolve(ServiceIdentity.ParticipantManager).get(params.conversation.id);
        });
    }
    /**
     * 分页从服务端获取群成员
     * @param params.cursor 偏移量，默认从0开始
     * @param params.limit 需要拉取的数量，默认50
     * @param params.conversation 会话
     */
    getConversationParticipantsByPage(params) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            let cursor = (_a = params.cursor) !== null && _a !== void 0 ? _a : Long.ZERO;
            const limit = (_b = params.limit) !== null && _b !== void 0 ? _b : 50;
            const response = yield this.api.GetConversationParticipantsList({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                cursor: cursor ? Long.fromValue(cursor) : Long.ZERO,
                limit,
                inboxType: params.conversation.inboxType
            });
            const resp = (_d = (_c = response.body) === null || _c === void 0 ? void 0 : _c.conversation_participants_body) === null || _d === void 0 ? void 0 : _d.participants_page;
            const hasMore = (_e = resp === null || resp === void 0 ? void 0 : resp.has_more) !== null && _e !== void 0 ? _e : false;
            cursor = (_f = resp === null || resp === void 0 ? void 0 : resp.cursor) !== null && _f !== void 0 ? _f : Long.ZERO;
            const participants = [];
            resp === null || resp === void 0 ? void 0 : resp.participants.forEach(p => {
                participants.push(Participant.fromServerParticipant(this.ctx, p, params.conversation, response.log_id));
            });
            this.resolve(ServiceIdentity.ParticipantManager).upsertBatch(params.conversation.id, participants);
            this.resolve(ServiceIdentity.ConversationManager).upsert(params.conversation);
            return {
                participants,
                hasMore,
                cursor
            };
        });
    }
    /**
     * 异步更新会话内各个成员的消息读取进度
     * @param params.conversation 会话
     * @param params.batchFetch
     */
    updateConversationReadReceipt(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { readIndexs, minIndexs } = yield this.getReadReceipt({
                conversation: params.conversation,
                batchFetch: params.batchFetch
            });
            const participants = this.resolve(ServiceIdentity.ParticipantManager).getRaw(params.conversation.id);
            let isUpdate = false;
            readIndexs.forEach(element => {
                const participant = participants.filter(p => p.userId === element.user_id.toString())[0];
                if (participant) {
                    if (!participant.readIndex.equals(element.index)) {
                        isUpdate = true;
                    }
                    participant.readIndex = element.index;
                }
            });
            minIndexs.forEach(element => {
                const participant = participants.filter(p => p.userId === element.user_id.toString())[0];
                if (participant) {
                    if (!participant.minIndex.equals(element.index)) {
                        isUpdate = true;
                    }
                    participant.minIndex = element.index;
                }
            });
            if (isUpdate) {
                this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationUpsert, this, params.conversation);
                this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
                participants.forEach(item => this.resolve(ServiceIdentity.ParticipantManager).upsert(params.conversation.id, item));
            }
        });
    }
    // #region User Action Extensions
    /**
     * 发送消息状态 已废弃请勿使用 保留做兼容
     */
    sendUserAction(params) {
        return __awaiter(this, void 0, void 0, function* () {
            /* istanbul ignore next */
            yield this.api.SendUserAction({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                extra: params.ext,
                actionType: params.actionType,
                inboxType: params.conversation.inboxType
            });
        });
    }
    /**
     * 发送输入状态 已废弃请勿使用 保留做兼容
     */
    sendInputStatus(params) {
        return __awaiter(this, void 0, void 0, function* () {
            /* istanbul ignore next */
            yield this.api.SendInputStatus({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                extra: params.ext,
                status: params.status,
                inboxType: params.conversation.inboxType
            });
        });
    }
    /**
     * 发送点对点消息 新版状态消息 请使用这个
     * @param params.conversation 会话
     * @param params.sendType 发送类型 0会话 1用户
     * @param params.msgType 消息类型
     * @param params.content 消息内容
     * @param params.ext 拓展信息
     * @param params.visibleUser 可见用户
     * @param params.invisibleUser 不可见用户
     */
    sendP2PMessage(params) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            /* istanbul ignore next */
            yield this.api.SendP2PMessage({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromValue(params.conversation.shortId),
                conversationType: params.conversation.type,
                sendType: params.sendType,
                msgType: params.msgType,
                content: params.content,
                clientId: uuidv4(),
                ext: (_a = params.ext) !== null && _a !== void 0 ? _a : {},
                visibleUser: (_c = (_b = params.visibleUser) === null || _b === void 0 ? void 0 : _b.map(u => Long.fromValue(u))) !== null && _c !== void 0 ? _c : [],
                invisibleUser: (_e = (_d = params.invisibleUser) === null || _d === void 0 ? void 0 : _d.map(u => Long.fromValue(u))) !== null && _e !== void 0 ? _e : [],
                inboxType: params.conversation.inboxType
            });
        });
    }
    // #endregion
    /**
     * 解散群会话
     * @param params.conversation 会话
     */
    dissolveConversation(params) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.api.DissolveConversation({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                inboxType: params.conversation.inboxType
            });
            this.resolve(ServiceIdentity.ConversationManager).delete(params.conversation.id);
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationDissolve, this, params.conversation);
            this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
        });
    }
    /**
     * 更新消息拓展信息
     * @param params.message 消息
     * @param params.ext 拓展信息
     */
    upsertMessageExt(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!params.message.serverId) {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.MessageOffline,
                    msg: 'message is offline',
                    reachServer: false,
                    sender: this
                });
            }
            // 本地覆盖下
            for (const key of Object.keys(params.ext)) {
                if (key.startsWith('s:')) {
                    delete params.ext[key];
                }
            }
            const conv = this.resolve(ServiceIdentity.ConversationManager).get(params.message.conversationId);
            yield this.api.ModifyMessageExt({
                conversationShortId: Long.fromValue(params.message.conversationShortId),
                messageId: Long.fromValue(params.message.serverId),
                ext: params.ext,
                ticket: conv.ticket,
                inboxType: conv.inboxType
            });
            params.message.ext = Object.assign(params.message.ext, params.ext);
            this.resolve(ServiceIdentity.MessageManager).upsert(params.message);
            return params.message;
        });
    }
    /**
     * 获用户的会话列表 从服务端拉取
     * @param params.type 会话类型 {@link ConversationType}
     * @param params.inboxType 信箱类型
     * @param params.cursor 拉取游标
     * @param params.limit 拉取条数
     * @param params.sortType 排序类型 1进群时间 2会话创建时间 3会话名称 4最新活跃时间
     * @param params.includeRole 查询群聊类型时，包含的群聊角色 {@link GroupRole}
     * @param params.excludeRole 查询群聊类型时，排除的群聊角色 {@link GroupRole}
     * @param params.withCold 忽略
     */
    getUserConversationList(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (params.inboxType === undefined) {
                params.inboxType = this.resolve(ServiceIdentity.InboxType).getDefaultInbox();
            }
            if (params.type !== im_proto.ConversationType.GROUP_CHAT &&
                (params.includeRole !== undefined || params.excludeRole !== undefined)) {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.InvalidParam,
                    msg: 'role filter only available in group chat',
                    sender: this,
                    reachServer: false
                });
            }
            else if (params.includeRole !== undefined && params.excludeRole !== undefined) {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.InvalidParam,
                    msg: 'conflict include and exclude',
                    sender: this,
                    reachServer: false
                });
            }
            const response = yield this.api.GetUserConversationList({
                type: params.type,
                cursor: params.cursor ? Long.fromValue(params.cursor) : Long.ZERO,
                limit: params.limit ? Long.fromValue(params.limit) : Long.fromNumber(20),
                sortType: (_a = params.sortType) !== null && _a !== void 0 ? _a : im_proto.SortType.JOIN_TIME,
                includeRole: params.includeRole,
                excludeRole: params.excludeRole,
                withCold: params.withCold,
                inboxType: params.inboxType
            });
            const result = (_b = response.body) === null || _b === void 0 ? void 0 : _b.get_conversation_list_body;
            const convs = result.list.map(r => Conversation.fromServerConversation(this.ctx, r, response.log_id));
            convs.forEach(c => this.resolve(ServiceIdentity.ConversationManager).upsert(c));
            return {
                conversation: convs,
                hasMore: result === null || result === void 0 ? void 0 : result.has_more,
                cursor: result === null || result === void 0 ? void 0 : result.next_cursor
            };
        });
    }
    // #region Unread Count Report
    /**
     * 上报本地未读数
     */
    unreadCountReport() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.ctx.option.unreadCountReport) {
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'unread count report not enabled',
                    sender: this,
                    type: ErrorType.InvalidParam,
                    reachServer: false
                });
            }
            const allConvs = this.resolve(ServiceIdentity.ConversationManager).getConversationArray();
            const groupByInbox = groupBy(allConvs, c => c.inboxType.toString());
            for (const inbox of Object.keys(groupByInbox)) {
                const convs = groupByInbox[inbox];
                const convReport = [];
                let total = 0;
                convs.forEach(c => {
                    const cUnread = this.getNormalMsgUnreadCount(c);
                    total += cUnread;
                    convReport.push({
                        shortId: Long.fromString(c.shortId),
                        count: Long.fromNumber(cUnread),
                        type: c.type
                    });
                });
                const reqBatch = chunkArray(convReport, 30);
                for (const batch of reqBatch) {
                    try {
                        yield this.api.UnreadCountReport({
                            total: Long.fromValue(total),
                            conv: batch,
                            inboxType: Number.parseInt(inbox, 10)
                        });
                    }
                    catch (err) {
                        /* istanbul ignore next */
                        Logger.ctxWarn(this.ctx, 'report unread error:', err, 'conv:', convs);
                    }
                }
            }
        });
    }
    /**
     * 从服务端通过inboxType获取未读数
     * @param params.inboxType 信箱类型
     */
    getServerUnreadCountByUser(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (params.inboxType === undefined) {
                /* istanbul ignore next */
                params.inboxType = this.resolve(ServiceIdentity.InboxType).getDefaultInbox();
            }
            const result = yield this.api.GetUnreadCount({
                total: true,
                inboxType: params.inboxType,
                shortIds: []
            });
            return (_b = (_a = result === null || result === void 0 ? void 0 : result.total_unread_count) === null || _a === void 0 ? void 0 : _a.toNumber()) !== null && _b !== void 0 ? _b : 0;
        });
    }
    /**
     * 从服务端获取会话未读数
     * @param params.conversation 希望获取的会话列表
     */
    getServerUnreadCountByConversation(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const unreadCountMap = new Map();
            const groupByInbox = groupBy(params.conversation, c => c.inboxType.toString());
            for (const inbox of Object.keys(groupByInbox)) {
                const shortIdsLong = groupByInbox[inbox].map(s => Long.fromString(s.shortId));
                const shortIdMap = new Map(groupByInbox[inbox].map(c => [c.shortId, c]));
                const result = yield this.api.GetUnreadCount({
                    total: false,
                    shortIds: shortIdsLong,
                    inboxType: Number.parseInt(inbox, 10)
                });
                for (const [k, v] of Object.entries(result.conv_unread_count)) {
                    const shortId = string2LongAsBit(k).toString();
                    if (shortIdMap.has(shortId)) {
                        unreadCountMap.set(shortIdMap.get(shortId), v.toNumber());
                    }
                }
            }
            return unreadCountMap;
        });
    }
    // #endregion
    // #region User Relation
    /**
     * 群禁言、取消群禁言 针对会话维度
     * @param params.conversation 会话
     * @param params.block 是否禁言
     * @param params.normalOnly 是否只禁言普通群成员
     */
    blockConversation(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (params.normalOnly === undefined) {
                params.normalOnly = false;
            }
            yield this.api.BlockConversation({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromValue(params.conversation.shortId),
                conversationType: params.conversation.type,
                status: params.block ? im_proto.BlockStatus.BLOCK : im_proto.BlockStatus.UNBLOCK,
                normalOnly: params.normalOnly,
                inboxType: params.conversation.inboxType
            });
            yield this.resolve(ServiceIdentity.ConversationManager).refreshAsync(params.conversation);
            return params.conversation;
        });
    }
    /**
     * 禁言群成员
     * @param params.conversation 会话
     * @param params.block 是否禁言
     * @param params.blockDuration key 是用户 id，value 是禁言时间，单位秒。 {111: 1000} 表示把用户 111 禁言 1000 秒
     * @param params.bizExt 拓展信息
     */
    blockMember(params) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const duration = {};
            if (params.blockDuration !== undefined) {
                Object.keys(params.blockDuration).forEach(k => {
                    duration[k] = Long.fromValue(params.blockDuration[k]);
                });
            }
            const result = yield this.api.BlockMember({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromValue(params.conversation.shortId),
                conversationType: params.conversation.type,
                status: params.block ? im_proto.BlockStatus.BLOCK : im_proto.BlockStatus.UNBLOCK,
                time: duration,
                bizExt: (_a = params.bizExt) !== null && _a !== void 0 ? _a : {},
                inboxType: params.conversation.inboxType
            });
            const localParticipants = this.resolve(ServiceIdentity.ParticipantManager).getMap(params.conversation.id);
            for (const userId of Object.keys((_b = params.blockDuration) !== null && _b !== void 0 ? _b : {})) {
                if (!((_c = result === null || result === void 0 ? void 0 : result.failed_members) === null || _c === void 0 ? void 0 : _c.find(i => Long.fromValue(userId).eq(i))) && localParticipants.has(userId)) {
                    const p = localParticipants.get(userId);
                    p.blocked = params.block; // TODO: left block time calculation
                    this.resolve(ServiceIdentity.ParticipantManager).upsert(params.conversation.id, p);
                }
            }
            return params.conversation;
        });
    }
    /**
     * 获取当前用户的黑名单
     * @param params.cursor 分页序号，从 0 开始
     * @param params.limit 分页的 pageSize
     * @param params.inboxType 信箱类型
     */
    getBlockList(params = {}) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (params.inboxType === undefined) {
                params.inboxType = this.resolve(ServiceIdentity.InboxType).getDefaultInbox();
            }
            const result = yield this.api.GetBlockList({
                cursor: params.cursor ? Long.fromValue(params.cursor) : Long.ZERO,
                limit: (_a = params.limit) !== null && _a !== void 0 ? _a : 20,
                inboxType: params.inboxType
            });
            return {
                blockList: result.user_info.map(u => ({
                    userId: u.user_id.toString(),
                    createTime: u.create_time.toString()
                })),
                hasMore: result === null || result === void 0 ? void 0 : result.has_more,
                cursor: result === null || result === void 0 ? void 0 : result.next_cursor
            };
        });
    }
    /* istanbul ignore next */
    /**
     * 拉黑用户 拉黑功能需要业务方的服务端配合接入
     * @param params.value 是否拉黑
     * @param params.userId 用户userid数组
     * @param params.inboxType 信箱类型
     */
    setBlockList(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (params.inboxType === undefined) {
                params.inboxType = this.resolve(ServiceIdentity.InboxType).getDefaultInbox();
            }
            yield this.api.SetBlockList({
                applySet: params.value,
                blocklist: params.userId.map(u => Long.fromValue(u)),
                inboxType: params.inboxType
            });
        });
    }
    /**
     * 查询用户是否被拉黑
     * @param params.userId 要查询用户的userid
     * @param params.inboxType 信箱类型
     */
    checkInBlockList(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (params.inboxType === undefined) {
                params.inboxType = this.resolve(ServiceIdentity.InboxType).getDefaultInbox();
            }
            const result = yield this.api.CheckInBlockList({
                userToCheck: Long.fromValue(params.userId),
                inboxType: params.inboxType
            });
            return result.in_blocklist;
        });
    }
    // #endregion
    // #region Broadcast
    /**
     * 超大群创建信息 发送消息统一使用 sendMessage，消息不存储到 SDK 中
     * @param params.conversation 会话
     * @param params.type 消息类型
     * @param params.content 消息体
     * @param params.ext 拓展信息
     * @param params.mentionedUsers  消息里包含的被 @ 用户
     * @param params.insert 创建完成是否process处理(新消息处理流程)
     */
    broadcastCreateMessage(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const ext = Object.assign({}, params.ext);
            const message = Message.createClientMessage(this.ctx, {
                type: params.type,
                content: params.content,
                ext,
                id: uuidv4(),
                conversationId: params.conversation.id,
                mentionedUsers: params.mentionedUsers || [],
                conversationShortId: params.conversation.shortId,
                conversationType: params.conversation.type
            });
            message.flightStatus = FlightStatus.Created;
            message.indexInConversation = params.conversation.lastMessageIndex.add(1);
            message.orderInConversation = params.conversation.lastMessageOrder.add(1);
            if (params.insert === undefined || params.insert) {
                yield this.resolve(ServiceIdentity.BroadcastManager).processNewMessage(message, MessageSource.Offline);
            }
            message.sendFunc = this.broadcastSendMessage.bind(this);
            return message;
        });
    }
    /**
     * 超大群接收消息 消息的统一出口 sdk内读不到消息
     * @param params.conversation 会话
     * @param params.cursor 游标
     * @param params.limit 单次拉去消息数量
     * @param params.reverse true加载旧消息，false加载新消息
     */
    broadcastReceiveMessage(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield this.broadcastReceiveMessageWithType(params);
            return {
                msgs: res.msgs,
                hasMore: res.hasMore,
                cursor: res.cursor
            };
        });
    }
    /**
     * 获取超大群用户数
     * @param params.conversation 会话
     */
    broadcastUserCounter(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const convs = params.conversation.map(c => ({
                shortId: Long.fromString(c.shortId),
                type: c.type
            }));
            const result = yield this.api.BroadcastUserCounter({
                conversations: convs
            });
            const res = {};
            for (const m of result.infos) {
                res[m.conversation_short_id.toString()] = m.counter;
            }
            return res;
        });
    }
    /**
     * 从服务端拉取超大群信息
     * @param params.conversationId 会话id
     */
    broadcastGetConversationOnline(params) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolve(ServiceIdentity.BroadcastManager).getWithOnline(params.conversationId, params.conversationId, im_proto.ConversationType.BROADCAST_CHAT);
        });
    }
    /* istanbul ignore next */
    /**
     * 从本地拉取超大群信息
     * @param params.conversationId 会话id
     */
    broadcastGetConversation(params) {
        return this.resolve(ServiceIdentity.BroadcastManager).getConversation(params.conversationId);
    }
    // #endregion
    /**
     * 给消息设置属性 一般的使用场景有：类似 lark 的给消息点赞、加急消息等。
     * 消息加急操作本质上是修改消息属性，在中台侧它只是一个状态变更，加急之后的附加操作（比如全局提醒，短信电话提醒等）需要业务方自己处理
     * @param params.message 消息
     * @param params.modifyContent  key:对应lark的表情, 例如ok/点赞/done... "e:love" operation: 操作类型 value:对应content, 也可以什么都不存, 只代表有这个key idempotentId:幂等id, 通常是user id,防止对单个点赞动作重复操作
     */
    modifyMessageProperty(params) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const result = {
                success: false,
                payload: params.message
            };
            const conv = this.resolve(ServiceIdentity.ConversationManager).getRaw(params.message.conversationId);
            if (!conv) {
                /* istanbul ignore next */
                return result;
            }
            // 寻找已经存在的属性, 计算operation
            for (const rawOp of params.modifyContent) {
                const existProp = (_b = (_a = params.message.property[rawOp.key]) === null || _a === void 0 ? void 0 : _a.findIndex(p => { var _a; return p.idempotentId === ((_a = rawOp.idempotentId) !== null && _a !== void 0 ? _a : this.ctx.option.userId); })) !== null && _b !== void 0 ? _b : -1;
                if (!rawOp.operation) {
                    if (existProp === -1) {
                        // 不存在则设置成添加
                        rawOp.operation = im_proto.OPERATION_TYPE.ADD_PROPERTY_ITEM;
                    }
                    else {
                        // 存在则设置成删除
                        rawOp.operation = im_proto.OPERATION_TYPE.REMOVE_PROPERTY_ITEM;
                    }
                }
            }
            // write property to local
            const ops = params.modifyContent.map(m => {
                var _a;
                return (Object.assign(Object.assign({}, m), { idempotentId: (_a = m.idempotentId) !== null && _a !== void 0 ? _a : this.ctx.option.userId, operation: m.operation // 有计算逻辑后一定存在
                 }));
            });
            MessageProperty.mergeOperationToCurrent(params.message, ops);
            this.resolve(ServiceIdentity.MessageManager).upsert(params.message);
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.MessagePropertyUpsert, this, params.message);
            let resp;
            try {
                resp = yield this.api.ModifyMessageProperty({
                    conversationId: conv.id,
                    conversationShortId: Long.fromValue(conv.shortId),
                    conversationType: conv.type,
                    serverId: Long.fromValue(params.message.serverId),
                    clientId: params.message.clientId,
                    ticket: conv.ticket,
                    modifyContent: ops,
                    inboxType: conv.inboxType
                });
            }
            catch (err) {
                result.success = false;
                // FIXME: 进 catch 的话 resp 应该必然是 undefined ？
                result.statusCode = resp === null || resp === void 0 ? void 0 : resp.status;
                result.statusMsg = err.msg;
                result.body = resp;
                // 清理刚才修改的item
                for (const rawOp of params.modifyContent) {
                    const existProp = (_d = (_c = params.message.property[rawOp.key]) === null || _c === void 0 ? void 0 : _c.findIndex(p => { var _a; return p.idempotentId === ((_a = rawOp.idempotentId) !== null && _a !== void 0 ? _a : this.ctx.option.userId); })) !== null && _d !== void 0 ? _d : -1;
                    if (existProp !== -1) {
                        // 本地有写入, 修改成fail状态
                        params.message.property[rawOp.key][existProp].status = PropertyStatus.Failed;
                    }
                }
                this.resolve(ServiceIdentity.MessageManager).upsert(params.message);
                this.resolve(ServiceIdentity.EventBus).emit(IMEvent.MessagePropertyUpsert, this, params.message);
                return result;
            }
            // calculate status
            if ([
                im_proto.ModifyMessagePropertyStatus.MODIFY_PROPERTY_SUCCESS,
                im_proto.ModifyMessagePropertyStatus.MODIFY_PROPERTY_REPEAT_REQUEST
            ].includes(resp === null || resp === void 0 ? void 0 : resp.status)) {
                result.success = true;
            }
            params.message.version = resp === null || resp === void 0 ? void 0 : resp.version;
            this.resolve(ServiceIdentity.MessageManager).upsert(params.message);
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.MessagePropertyUpsert, this, params.message);
            result.checkCode = Long.ZERO;
            result.checkMsg = '';
            result.statusCode = resp.status;
            result.statusMsg = '';
            result.body = resp;
            return result;
        });
    }
    // #region Mark Message
    /* istanbul ignore next */
    /**
     * 标记消息 pin、收藏等
     * @param params.message 消息
     * @param params.actionType 标记类型 UNKNOWN_ACTION = 0 未知,FAVORITE = 1收藏,PIN = 2 pin,UNREAD = 3 未读
     * @param params.doAction 是否标记 true 表示标记，false 表示取消标记
     * @param params.sortTime 0-pin时间，1-消息发送时间
     * @param params.tag pin 的分类, 非必传
     */
    markMessage(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const conv = this.resolve(ServiceIdentity.ConversationManager).get(params.message.conversationId);
            if (params.message.isOffline) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.InvalidParam,
                    sender: this,
                    msg: 'offline message cant be marked',
                    reachServer: false
                });
            }
            yield this.api.MarkMessage({
                conversationId: params.message.conversationId,
                conversationShortId: Long.fromValue(params.message.conversationShortId),
                conversationType: params.message.conversationType,
                serverMessageId: Long.fromValue(params.message.serverId),
                actionType: params.actionType,
                doAction: (_a = params.doAction) !== null && _a !== void 0 ? _a : true,
                sortTime: Long.fromValue((_b = params.sortTime) !== null && _b !== void 0 ? _b : Long.ZERO),
                tag: params.tag !== undefined ? Long.fromValue(params.tag) : undefined,
                inboxType: conv.inboxType
            });
        });
    }
    /**
     * 获取标记消息列表
     * @param params.conversation 会话
     * @param params.actionType 标记类型 {@link ActionType}
     * @param params.cursor 获取消息起始位置游标
     * @param params.ascending 是否升序排列，默认降序(按时间减小的方向拉)
     * @param params.limit 每次拉取消息条数
     * @param params.tag  如果想获取某一特定 tag 下的 pin 消息，此参数是必传的，如果要获取所有的 pin 消息，此参数不传或者传 -1
     */
    pullMarkMessage(params) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.api.PullMarkMessage({
                conversationId: (_a = params.conversation) === null || _a === void 0 ? void 0 : _a.id,
                conversationShortId: ((_b = params.conversation) === null || _b === void 0 ? void 0 : _b.shortId) === undefined ? undefined : Long.fromValue(params.conversation.shortId),
                conversationType: (_c = params.conversation) === null || _c === void 0 ? void 0 : _c.type,
                cursor: Long.fromValue((_d = params.cursor) !== null && _d !== void 0 ? _d : Long.ZERO),
                asc: (_e = params.ascending) !== null && _e !== void 0 ? _e : false,
                limit: Long.fromValue((_f = params.limit) !== null && _f !== void 0 ? _f : 50),
                actionType: params.actionType,
                tag: params.tag !== undefined ? Long.fromValue(params.tag) : undefined,
                inboxType: (_h = (_g = params.conversation) === null || _g === void 0 ? void 0 : _g.inboxType) !== null && _h !== void 0 ? _h : 0
            });
            const body = response.body.pull_mark_message_body;
            const msgs = [];
            body.messages.forEach(m => {
                const msg = Message.fromServerMessage(this.ctx, m, response.log_id);
                msgs.push(msg);
            });
            return {
                message: msgs,
                hasMore: body.has_more,
                cursor: body.next_cursor
            };
        });
    }
    /* istanbul ignore next */
    /**
     * 批量unpin消息
     * @param params.messages 消息列表
     * @param params.actionType mark 类型，pin 对应的类型为 ActionType.PIN
     * @param params.tag 批量unpin某 tag 下的消息
     */
    batchUnmarkMessage(params) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (params.messages === undefined || params.messages.length === 0) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.InvalidParam,
                    msg: 'invalid message list',
                    sender: this
                });
            }
            // TODO: multi conversation support (group by conversation id)
            const res = yield this.api.BatchUnmarkMessage({
                conversationId: params.messages[0].conversationId,
                conversationShortId: Long.fromValue(params.messages[0].conversationShortId),
                conversationType: params.messages[0].conversationType,
                serverMessageIds: params.messages.map(message => Long.fromValue(message.serverId)),
                actionType: params.actionType,
                tag: params.tag !== undefined ? Long.fromValue(params.tag) : undefined
            });
            const success = res.status === 0;
            return {
                success,
                checkCode: res.check_code,
                checkMsg: res.check_message,
                statusCode: (_a = res.status) !== null && _a !== void 0 ? _a : -1,
                body: res,
                payload: params.messages
            };
        });
    }
    /* istanbul ignore next */
    /**
     * 上报会话内消息未读数
     * @param params.conversation 会话
     * @param params.totalUnreadCount 总未读数
     * @param params.tagUnreadCount tag标签未读数
     */
    markMessageUnreadCountReport(params) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const tagUnreadCountMap = {};
            if (params.tagUnreadCount !== undefined) {
                for (const tag of Object.keys(params.tagUnreadCount)) {
                    tagUnreadCountMap[tag] = Long.fromNumber(params.tagUnreadCount[tag]);
                }
            }
            const res = yield this.api.MarkMsgUnreadCountReport({
                conversationId: (_a = params === null || params === void 0 ? void 0 : params.conversation) === null || _a === void 0 ? void 0 : _a.id,
                conversationShortId: ((_b = params === null || params === void 0 ? void 0 : params.conversation) === null || _b === void 0 ? void 0 : _b.shortId) === undefined ? undefined : Long.fromValue(params === null || params === void 0 ? void 0 : params.conversation.shortId),
                conversationType: (_c = params === null || params === void 0 ? void 0 : params.conversation) === null || _c === void 0 ? void 0 : _c.type,
                totalUnreadCount: params.totalUnreadCount !== undefined ? Long.fromNumber(params.totalUnreadCount) : undefined,
                tagUnreadCount: tagUnreadCountMap
            });
            return {
                setTotalStatus: res.set_total_status,
                failedTagList: res.failed_tag_list
            };
        });
    }
    /**
     * 获取标记消息的未读数
     * @param params.conversation 会话
     * @param params.getTotal 是否获取总的未读数，传 false 表示只获取当前 tag 对应的 pin 消息的未读数
     * @param params.tags tag 列表，用于获取某些 tag 下的未读数
     */
    markMessageGetUnreadCount(params) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield this.api.MarkMsgGetUnreadCount({
                conversationId: (_a = params === null || params === void 0 ? void 0 : params.conversation) === null || _a === void 0 ? void 0 : _a.id,
                conversationShortId: ((_b = params === null || params === void 0 ? void 0 : params.conversation) === null || _b === void 0 ? void 0 : _b.shortId) === undefined ? undefined : Long.fromValue(params === null || params === void 0 ? void 0 : params.conversation.shortId),
                conversationType: (_c = params === null || params === void 0 ? void 0 : params.conversation) === null || _c === void 0 ? void 0 : _c.type,
                getTotal: (_d = params.getTotal) !== null && _d !== void 0 ? _d : false,
                tags: (_e = params.tags) === null || _e === void 0 ? void 0 : _e.map(item => Long.fromValue(item))
            });
            const tag_unread_count = (_f = res.tag_unread_count) !== null && _f !== void 0 ? _f : {};
            const newTagUnreadCount = {};
            for (const item of Object.keys(tag_unread_count)) {
                newTagUnreadCount[string2LongAsBit(item).toString()] = tag_unread_count[item].toNumber();
            }
            return {
                tagUnreadCount: newTagUnreadCount,
                totalCount: res.total_count,
                failedTagList: res.failed_tag_list
            };
        });
    }
    // #endregion
    // #region Reference Message
    /* istanbul ignore next */
    /**
     * 获取消息的实时信息
     * @param params.conversation 会话
     * @param params.serverMessageId 消息的serverid
     */
    getMessageByServerId(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            // find message from local first
            const local = this.resolve(ServiceIdentity.MessageManager).getByServerIdRaw(params.conversation.id, params.serverMessageId);
            if (local) {
                return local;
            }
            try {
                const res = yield this.api.GetMessageByServerId({
                    conversationId: params.conversation.id,
                    conversationShortId: Long.fromString(params.conversation.shortId),
                    conversationType: params.conversation.type,
                    serverId: Long.fromString(params.serverMessageId),
                    inboxType: params.conversation.inboxType
                });
                const messageInfo = (_b = (_a = res.body) === null || _a === void 0 ? void 0 : _a.get_message_by_id_body) === null || _b === void 0 ? void 0 : _b.msg_info;
                if (!messageInfo.body) {
                    return null;
                }
                return Message.fromServerMessage(this.ctx, messageInfo.body, res.log_id);
            }
            catch (error) {
                return null;
            }
        });
    }
    /**
     * 获取引用消息链。比如 消息 B 引用了 消息 A，消息 C 引用了消息 B，那么 A -> B -> C 就是一条引用消息链，通过 A、B、C 任意一条消息都可以获取这条消息链
     * @param params.message
     */
    getMessageReferenceList(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            let realRoot;
            if (params.message.isRootReference) {
                realRoot = params.message;
            }
            else {
                // try to find the real root message
                const conv = this.resolve(ServiceIdentity.ConversationManager).get(params.message.conversationId);
                const refRootMessageId = (_b = (_a = params.message.referenceInfo) === null || _a === void 0 ? void 0 : _a.root_message_id) === null || _b === void 0 ? void 0 : _b.toString();
                if (refRootMessageId === undefined) {
                    return [];
                }
                const rootRefMsg = yield this.getMessageByServerId({
                    conversation: conv,
                    serverMessageId: refRootMessageId
                });
                if (!rootRefMsg) {
                    /* istanbul ignore next */
                    return [];
                }
                realRoot = rootRefMsg;
            }
            const result = [realRoot];
            // calculate ref list
            result.push(...this.resolve(ServiceIdentity.MessageManager)
                .getList(realRoot.conversationId)
                .filter(msg => { var _a, _b; return ((_b = (_a = msg.referenceInfo) === null || _a === void 0 ? void 0 : _a.root_message_id) === null || _b === void 0 ? void 0 : _b.toString()) === realRoot.serverId; }));
            return result;
        });
    }
    /**
     * 语音转文字
     * 1.目前仅支持wav格式语音转文字功能，转换后的文字会在返回的message->content内部的ext  's:file_ext_key_audio_asr_text'拓展字段中，并会储存在消息内部的拓展字段中，再次转换相同的语音时sdk也会优先返回缓存的字段。
     * 2.拥有命中敏感词功能，需要到tcc上配置，命中时返回check_code和check_message字段会存在message体里的ext字段中分别是：'s:recognition_response_check_code'和's:recognition_response_check_msg'。（注意转换后的文字和命中的code在不同的ext中）
     * 3.因为缓存功能，当某一用户调用此api转换语言后，此条消息里的s:file_ext_key_audio_asr_text会一直保存转换后的文字，且共享此消息的其他群成员拿到的也是最新的message，业务需要根据自己场景来实现转换后的文字展示效果。
     */
    convertVoiceToText(message) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!(message === null || message === void 0 ? void 0 : message.serverId)) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.MPInvalidArgument,
                    msg: 'message is offline',
                    reachServer: false,
                    sender: this
                });
            }
            // 判断消息类型
            if ((message === null || message === void 0 ? void 0 : message.type) !== im_proto.MessageType.MESSAGE_TYPE_AUDIO) {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.InvalidParam,
                    msg: 'message is not audio',
                    reachServer: false,
                    sender: this
                });
            }
            const files = (_a = JSON.parse(message === null || message === void 0 ? void 0 : message.content)) === null || _a === void 0 ? void 0 : _a.__files;
            const keys = Object.keys(files);
            const { type, ext } = files[keys[0]]; // 语音消息url
            // 判断音频消息格式目前server仅支持wav格式
            if (type !== 'wav') {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.InvalidParam,
                    msg: 'audio content is not wav',
                    reachServer: false,
                    sender: this
                });
            }
            // 判断消息是否已转换
            if (ext[InfoKeys.FileExtKeyAudioAsrText]) {
                /* istanbul ignore next */
                return message;
            }
            try {
                const response = yield this.api.ConvertVoiceToText({
                    content: message === null || message === void 0 ? void 0 : message.content,
                    uid: Long.fromValue(this.ctx.option.userId),
                    server_message_id: Long.fromValue(message === null || message === void 0 ? void 0 : message.serverId),
                    audioOptions: {
                        vid: ext[FileExtKey.Vid]
                    },
                    conv_short_id: Long.fromValue(message === null || message === void 0 ? void 0 : message.conversationShortId)
                });
                const { audio, check_code, check_message } = response === null || response === void 0 ? void 0 : response.audio_recognition_body;
                // 透传给用户的敏感词命中字段
                if (check_code && check_message) {
                    message.ext[InfoKeys.RecognitionResponseCheckCode] = check_code === null || check_code === void 0 ? void 0 : check_code.toString();
                    message.ext[InfoKeys.RecognitionResponseCheckMsg] = check_message === null || check_message === void 0 ? void 0 : check_message.toString();
                }
                // server 将把转好的 txt 放在了 content 里的拓展里面
                message.content = audio === null || audio === void 0 ? void 0 : audio.content;
                this.resolve(ServiceIdentity.MessageManager).upsert(message);
                return message;
            }
            catch (error) {
                /* istanbul ignore next */
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.ServerError,
                    msg: 'Voice conversion failed',
                    reachServer: false,
                    sender: this
                });
            }
        });
    }
    // #endregion
    install() {
        this.api = new ExtensionApiManager(this.getContext());
        this.ctx.register(ServiceIdentity.BroadcastManager, new BroadcastManager(this.ctx));
        this.ctx.register(ServiceIdentity.ExtensionApi, this.api);
        this.ctx.register(ServiceIdentity.ExtensionPlugin, this);
        this.instance.deleteConversation = this.extendFunc(this.deleteConversation);
        this.instance.deleteMessage = this.extendFunc(this.deleteMessage);
        this.instance.leaveConversation = this.extendFunc(this.leaveConversation);
        this.instance.getMessageReadReceipt = this.extendFunc(this.getMessageReadReceipt);
        this.instance.getConversationListByTop = this.extendFunc(this.getConversationListByTop);
        this.instance.getConversationListByFavorite = this.extendFunc(this.getConversationListByFavorite);
        this.instance.addParticipants = this.extendFunc(this.addParticipants);
        this.instance.removeParticipants = this.extendFunc(this.removeParticipants);
        this.instance.updateParticipant = this.extendFunc(this.updateParticipant);
        this.instance.setConversationSettingInfo = this.extendFunc(this.setConversationSettingInfo);
        this.instance.setConversationCoreInfo = this.extendFunc(this.setConversationCoreInfo);
        this.instance.upsertConversationSettingExtInfo = this.extendFunc(this.upsertConversationSettingExtInfo);
        this.instance.upsertConversationCoreExtInfo = this.extendFunc(this.upsertConversationCoreExtInfo);
        this.instance.setConversationWeakMuteConfig = this.extendFunc(this.setConversationWeakMuteConfig);
        this.instance.getConversationParticipants = this.extendFunc(this.getConversationParticipants);
        this.instance.getConversationParticipantsAsync = this.extendFunc(this.getConversationParticipantsAsync);
        this.instance.getConversationParticipantsByPage = this.extendFunc(this.getConversationParticipantsByPage);
        this.instance.updateConversationReadReceipt = this.extendFunc(this.updateConversationReadReceipt);
        this.instance.sendUserAction = this.extendFunc(this.sendUserAction);
        this.instance.sendInputStatus = this.extendFunc(this.sendInputStatus);
        this.instance.upsertMessageExt = this.extendFunc(this.upsertMessageExt);
        this.instance.dissolveConversation = this.extendFunc(this.dissolveConversation);
        this.instance.modifyMessageProperty = this.extendFunc(this.modifyMessageProperty);
        this.instance.unreadCountReport = this.extendFunc(this.unreadCountReport);
        this.instance.getServerUnreadCountByConversation = this.extendFunc(this.getServerUnreadCountByConversation);
        this.instance.getServerUnreadCountByUser = this.extendFunc(this.getServerUnreadCountByUser);
        this.instance.getUserConversationList = this.extendFunc(this.getUserConversationList);
        this.instance.blockConversation = this.extendFunc(this.blockConversation);
        this.instance.blockMember = this.extendFunc(this.blockMember);
        this.instance.sendP2PMessage = this.extendFunc(this.sendP2PMessage);
        this.instance.setBlockList = this.extendFunc(this.setBlockList);
        this.instance.getBlockList = this.extendFunc(this.getBlockList);
        this.instance.checkInBlockList = this.extendFunc(this.checkInBlockList);
        this.instance.broadcastCreateMessage = this.extendFunc(this.broadcastCreateMessage);
        this.instance.broadcastReceiveMessage = this.extendFunc(this.broadcastReceiveMessage);
        this.instance.broadcastUserCounter = this.extendFunc(this.broadcastUserCounter);
        this.instance.broadcastGetConversationOnline = this.extendFunc(this.broadcastGetConversationOnline);
        this.instance.broadcastGetConversation = this.extendFunc(this.broadcastGetConversation);
        this.instance.markMessage = this.extendFunc(this.markMessage);
        this.instance.pullMarkMessage = this.extendFunc(this.pullMarkMessage);
        this.instance.getMessageByServerId = this.extendFunc(this.getMessageByServerId);
        this.instance.batchUnmarkMessage = this.extendFunc(this.batchUnmarkMessage);
        this.instance.markMessageUnreadCountReport = this.extendFunc(this.markMessageUnreadCountReport);
        this.instance.markMessageGetUnreadCount = this.extendFunc(this.markMessageGetUnreadCount);
        this.instance.getMessageReferenceList = this.extendFunc(this.getMessageReferenceList);
        this.instance.convertVoiceToText = this.extendFunc(this.convertVoiceToText);
        this.addEventHandler();
        this.resolve(ServiceIdentity.MessageManager).injectProcessor(new PropertyCmdProcessor(this.ctx));
        this.resolve(ServiceIdentity.MessageManager).injectProcessor(new BlockCmdProcessor(this.ctx));
        this.resolve(ServiceIdentity.MessageManager).injectProcessor(new PinCmdProcessor(this.ctx));
    }
    receivePacket(packet) {
        return __awaiter(this, void 0, void 0, function* () {
            if (packet.cmd === im_proto.IMCMD.NEW_BROADCAST_MSG_NOTIFY) {
                yield this.receiveBroadcastPacket(packet);
            }
            if (packet.cmd === im_proto.IMCMD.NEW_P2P_MSG_NOTIFY) {
                yield this.receiveP2PMessage(packet);
            }
        });
    }
    receiveP2PMessage(packet) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if ((_a = packet.body) === null || _a === void 0 ? void 0 : _a.has_new_p2p_message_notify) {
                const body = packet.body.has_new_p2p_message_notify;
                const msg = new P2PMessage();
                msg.sendType = body.send_type;
                msg.sender = body.sender.toString();
                msg.secSender = body.sec_sender;
                msg.conversationId = body.conversation_id;
                msg.conversationShortId = body.conversation_short_id.toString();
                msg.conversationType = body.conversation_type;
                msg.type = body.message_type;
                msg.content = body.content;
                msg.ext = body.ext;
                msg.createTime = new Date(body.create_time.toNumber());
                this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ReceiveNewP2PMessage, this, msg);
            }
        });
    }
    receiveBroadcastPacket(packet) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const body = packet.body.has_new_message_notify;
            const serverMsg = body.message;
            const msg = Message.fromServerMessage(this.ctx, serverMsg, packet.log_id);
            if (!msg.ext) {
                /* istanbul ignore next */
                msg.ext = {};
            }
            yield this.resolve(ServiceIdentity.BroadcastManager).processNewMessage(msg, MessageSource.Online);
            const conv = this.resolve(ServiceIdentity.BroadcastManager).getConversation(msg.conversationId);
            const oldCursor = conv.localIndex;
            conv.setConversationCursor(body.next_cursor);
            if (oldCursor.gt(Long.ZERO) && ((_a = body.previous_cursor) === null || _a === void 0 ? void 0 : _a.gt(oldCursor))) {
                let hasMore = true;
                let next_cursor = oldCursor;
                let i = 0;
                const limit = 5; // 最多拉取的次数，防止超大群消息过多
                while (hasMore && i < limit) {
                    const response = yield this.broadcastReceiveMessageWithType({
                        conversation: conv,
                        cursor: next_cursor,
                        limit: 50,
                        reverse: false,
                        pullType: 1 // sdk 触发的拉取
                    });
                    hasMore = response.hasMore;
                    next_cursor = response.cursor;
                    yield this.resolve(ServiceIdentity.BroadcastManager).processNewMessagesFromPull(response.msgs, MessageSource.BroadcastLoadMore, response.log_id);
                    i++;
                }
            }
        });
    }
    /* istanbul ignore next */
    dispose() {
        return __awaiter(this, void 0, void 0, function* () {
            this.resolve(ServiceIdentity.ParticipantManager).dispose();
            this.resolve(ServiceIdentity.BroadcastManager).dispose();
        });
    }
    addEventHandler() {
        // 自动根据消息计算最小readIndex, 可关闭, 默认打开
        if (this.ctx.option.autoReadIndex) {
            this.resolve(ServiceIdentity.EventBus).subscribe(IMEvent.MessageUpsert, (m) => __awaiter(this, void 0, void 0, function* () {
                /* istanbul ignore next */
                if (m.flightStatus === FlightStatus.Inflight) {
                    return;
                }
                const conv = this.resolve(ServiceIdentity.ConversationManager).getRaw(m.conversationId);
                if (!conv) {
                    return;
                }
                const par = this.resolve(ServiceIdentity.ParticipantManager).getRaw(conv.id);
                if (par.length === 0) {
                    return;
                }
                const sender = this.resolve(ServiceIdentity.ParticipantManager).getByUserIdRaw(conv.id, m.sender);
                // TODO: 构造个能触发这个分支的测试
                /* istanbul ignore next */
                if (sender && m.moveReadIndex) {
                    sender.readIndex = m.indexInConversation;
                    sender.readOrder = m.orderInConversation;
                    this.resolve(ServiceIdentity.ParticipantManager).upsert(conv.id, sender);
                    Logger.ctxDebug(this.ctx, `auto readindex with read:${sender.readIndex.toString()} & order:${sender.readOrder.toString()}`);
                }
            }));
        }
        this.resolve(ServiceIdentity.EventBus).subscribe(IMEvent.ReceiveNewP2PMessage, (message) => __awaiter(this, void 0, void 0, function* () {
            if (message.type === im_proto.MessageType.MESSAGE_TYPE_READ_COMMAND) {
                const conversation = this.resolve(ServiceIdentity.ConversationManager).getWithShortIdRaw(message.conversationShortId);
                if (!conversation) {
                    /* istanbul ignore next */
                    return;
                }
                const contentObj = JSONLong.parse(message.content);
                const sender = Long.fromValue(contentObj.P2PSender).toString();
                const participant = this.resolve(ServiceIdentity.ParticipantManager).getByUserIdRaw(conversation.id, sender);
                if (!participant) {
                    /* istanbul ignore next */
                    return;
                }
                const oldValue = participant.readIndex;
                const newValue = Long.fromValue(contentObj.P2PSenderReadIndex);
                if (oldValue.gte(newValue)) {
                    /* istanbul ignore next */
                    return;
                }
                participant.readIndex = newValue;
                this.resolve(ServiceIdentity.ParticipantManager).upsert(conversation.id, participant);
                // TODO: emit manually, refactor later
                this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ConversationUpsert, this, conversation);
                this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
                Logger.ctxDebug(this.ctx, `p2p update ${participant.userId} readindex: ${oldValue.toString()} to ${newValue.toString()}`);
            }
        }));
    }
    broadcastReceiveMessageWithType(params) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.api.BroadcastReceiveMessage({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                cursor: params.cursor ? Long.fromValue(params.cursor) : params.conversation.localIndex,
                limit: params.limit ? Long.fromValue(params.limit) : Long.fromNumber(20),
                reverse: (_a = params.reverse) !== null && _a !== void 0 ? _a : false,
                pullType: (_b = params.pullType) !== null && _b !== void 0 ? _b : 0,
                inboxType: params.conversation.inboxType
            });
            const result = (_c = response.body) === null || _c === void 0 ? void 0 : _c.broadcast_recv_message_body;
            const msgs = (_e = (_d = result.messages) === null || _d === void 0 ? void 0 : _d.map(m => Message.fromServerMessage(this.ctx, m, response.log_id))) !== null && _e !== void 0 ? _e : [];
            yield this.resolve(ServiceIdentity.BroadcastManager).processNewMessagesFromPull(msgs, MessageSource.BroadcastLoadMore, response.log_id);
            if (result.next_cursor) {
                params.conversation.setConversationCursor(result.next_cursor);
            }
            return {
                msgs,
                hasMore: result.has_more,
                cursor: result.next_cursor,
                log_id: response.log_id
            };
        });
    }
    broadcastSendMessage(message) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const c = this.resolve(ServiceIdentity.BroadcastManager).getConversation(message.conversationId);
            const result = {
                success: false,
                payload: message
            };
            message.flightStatus = FlightStatus.Inflight;
            try {
                const response = yield this.api.BroadcastSendMessage({
                    conversationId: message.conversationId,
                    conversationShortId: Long.fromString(message.conversationShortId),
                    conversationType: message.conversationType,
                    clientId: message.clientId,
                    content: message.content,
                    ticket: c.ticket,
                    ext: message.ext,
                    type: message.type,
                    mentionedUsers: message.mentionedUsers.map(u => Long.fromString(u)),
                    inboxType: c.inboxType
                });
                const res = (_a = response.body) === null || _a === void 0 ? void 0 : _a.broadcast_send_message_body;
                result.body = res;
                result.checkCode = res.check_code;
                result.checkMsg = res.check_message;
                result.statusCode = res.status;
                result.statusMsg = res.extra_info;
                if (this.ctx.option.debug) {
                    /* istanbul ignore next */
                    message.ext[InfoKeys.LocalLogId] = response.log_id;
                }
                /* istanbul ignore else */
                if (res.status === 0) {
                    const serverId = res.server_message_id.toString();
                    message.serverId = serverId;
                    message.flightStatus = FlightStatus.Succeeded;
                    result.success = true;
                    message.isOffline = false;
                }
                else {
                    // 消息送到了服务端, 但可能被拒绝了
                    message.flightStatus = FlightStatus.Rejected;
                    if (res.status === im_proto.SendMessageStatus.CHECK_MSG_NOT_PASS_BUT_SELF_VISIBLE) {
                        message.flightStatus = FlightStatus.SelfVisible;
                    }
                }
            }
            catch (e) {
                /* istanbul ignore next */
                message.flightStatus = FlightStatus.Failed;
            }
            return result;
        });
    }
    /** @private */
    getReadReceipt(params) {
        return __awaiter(this, void 0, void 0, function* () {
            /* istanbul ignore else */
            if (!params.batchFetch) {
                const readIndex = yield this.api.GetConversationParticipantsReadIndexV3({
                    conversationId: params.conversation.id,
                    conversationShortId: Long.fromString(params.conversation.shortId),
                    conversationType: params.conversation.type,
                    inboxType: params.conversation.inboxType
                });
                const minIndex = yield this.api.GetConversationParticipantsMinIndexV3({
                    conversationId: params.conversation.id,
                    conversationShortId: Long.fromString(params.conversation.shortId),
                    conversationType: params.conversation.type,
                    inboxType: params.conversation.inboxType
                });
                return {
                    readIndexs: (readIndex === null || readIndex === void 0 ? void 0 : readIndex.indexes) || [],
                    minIndexs: minIndex.indexes
                };
            }
            else {
                const { conversation } = params;
                const response = yield this.api.BatchGetConversationParticipantsReadIndex({
                    conversationId: [conversation.id],
                    conversationShortId: [Long.fromString(conversation.shortId)],
                    min_index_required: true
                });
                const conversationParticipantsReadIndex = response.conversationParticipantsReadIndex;
                const ParticipantReadIndex = (conversationParticipantsReadIndex === null || conversationParticipantsReadIndex === void 0 ? void 0 : conversationParticipantsReadIndex[0].participantReadIndex) || [];
                const readIndexs = [];
                const minIndexs = [];
                ParticipantReadIndex.forEach(item => {
                    if (item.index) {
                        readIndexs.push({
                            user_id: Long.fromString(item.user_id.toString()),
                            index: Long.fromString(item.index.toString())
                        });
                    }
                    minIndexs.push({
                        user_id: Long.fromString(item.user_id.toString()),
                        index: Long.fromString(item.index_min.toString())
                    });
                });
                return {
                    readIndexs,
                    minIndexs
                };
            }
        });
    }
    /**
     * 获取会话正常消息未读数
     * @param conversation 会话
     * @returns 未读数
     */
    getNormalMsgUnreadCount(conversation) {
        if (conversation.isMuted) {
            return 0;
        }
        if (conversation.pushStatus === PushStatus.PartAllow) {
            return conversation.unreadCountWithWhiteList;
        }
        return conversation.unreadCount;
    }
}

export { ExtensionPlugin };
//# sourceMappingURL=ExtensionPlugin.js.map
